import React, { useEffect, useState } from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";
import { styles } from "@withorbit/ui";
import { getFirebaseAuth } from "../util/firebaseAuth.web.js";
import { UserRecord } from "../authentication/index.js";

interface AuthAwareSignInButtonProps {
  color: string;
}

const componentStyles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    gap: styles.layout.gridUnit,
  },
  userEmail: {
    ...styles.type.labelSmall.typeStyle,
    color: styles.colors.ink,
    opacity: 0.7,
  },
  signOutButton: {
    backgroundColor: "rgba(0,0,0,0.15)",
    padding: 8,
    paddingHorizontal: 16,
    borderRadius: 8,
  },
  signOutText: {
    fontFamily: "Dr-Bold",
    fontSize: 17,
  },
});

export function AuthAwareSignInButton({ color }: AuthAwareSignInButtonProps): JSX.Element | null {
  // undefined = loading, null = signed out, UserRecord = signed in
  const [userRecord, setUserRecord] = useState<UserRecord | null | undefined>(undefined);

  useEffect(() => {
    const auth = getFirebaseAuth();
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setUserRecord({
          userID: user.uid,
          emailAddress: user.email,
        });
      } else {
        setUserRecord(null);
      }
    });
    return () => unsubscribe();
  }, []);

  const handleSignOut = async () => {
    try {
      const auth = getFirebaseAuth();
      await auth.signOut();
    } catch (error) {
      console.error("Sign out error:", error);
    }
  };

  // Loading state - show nothing
  if (userRecord === undefined) {
    return null;
  }

  // Signed out - show sign in link
  if (userRecord === null) {
    return (
      <a
        href="/login"
        style={{
          backgroundColor: "rgba(0,0,0,0.15)",
          padding: "8px 16px",
          borderRadius: 8,
          textDecoration: "none",
          color: color,
          fontFamily: "Dr-Bold",
          fontSize: 17,
        }}
      >
        Sign In
      </a>
    );
  }

  // Signed in - show user info and sign out button
  return (
    <View style={componentStyles.container}>
      <Text style={[componentStyles.userEmail, { color }]}>
        {userRecord.emailAddress}
      </Text>
      <Pressable onPress={handleSignOut} style={componentStyles.signOutButton}>
        <Text style={[componentStyles.signOutText, { color }]}>Sign Out</Text>
      </Pressable>
    </View>
  );
}

export default AuthAwareSignInButton;
